import { Button, Card, Typography } from '@sl/react-datafabric-components';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSubscribeMutation } from '../../../../common/redux/services/subscriptions';
import { useGetTransactionQuery } from '../../../../common/redux/services/transactions';
import getErrorMessage from '../../../../common/utils/get-error-message';

export function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionID = searchParams.get('session_id');
  const isDowngrade = searchParams.get('downgrade') === 'true';
  const lastConfirmedSessionRef = useRef<string | null>(null);
  const [status, setStatus] = useState<
    | 'verifiying-payment'
    | 'lost'
    | 'confirm-payment'
    | 'confirm-downgrade'
    | 'failed'
    | undefined
  >();
  const [failureDetail, setFailureDetail] = useState<string>('');

  const {
    data: item,
    error,
    isFetching: isTransactionFetching,
    isLoading: isTransactionLoading,
  } = useGetTransactionQuery(sessionID, {
    skip: !sessionID,
  });

  const [createSubscription] = useSubscribeMutation();

  useEffect(() => {
    if (!sessionID) {
      return;
    }

    const storedSessionId = sessionStorage.getItem('lastSessionId');
    if (storedSessionId !== sessionID) {
      sessionStorage.removeItem('subscriptionConfirmed');
      sessionStorage.setItem('lastSessionId', sessionID);
      lastConfirmedSessionRef.current = null;
    }
  }, [sessionID]);

  useEffect(() => {
    if (isDowngrade) {
      setStatus('confirm-downgrade');
      return;
    }

    if (!sessionID) {
      setStatus('lost');
      return;
    }

    if (sessionStorage.getItem('subscriptionConfirmed')) {
      setStatus('confirm-payment');
      return;
    }

    if (isTransactionLoading || isTransactionFetching) {
      setStatus('verifiying-payment');
      return;
    }

    if (error) {
      setFailureDetail(
        getErrorMessage(error) ||
          'We could not verify your payment details. Please try again.'
      );
      setStatus('failed');
      return;
    }

    const metadata = item?.data?.metadata as Record<string, any> | undefined;
    if (!metadata) {
      setFailureDetail(
        'Payment details were not found for this session. Please try again.'
      );
      setStatus('failed');
      return;
    }

    if (lastConfirmedSessionRef.current === sessionID) {
      return;
    }

    lastConfirmedSessionRef.current = sessionID;

    async function confirmPayment() {
      setStatus('verifiying-payment');
      setFailureDetail('');

      const planInfo = metadata.planInfo || {};
      const selectedPriceId =
        planInfo.priceID ||
        planInfo.priceId ||
        metadata?.providerMetadata?.priceID ||
        metadata?.providerMetadata?.priceId;
      const selectedBillingCycle =
        planInfo.billingCycle || metadata?.providerMetadata?.billingCycle;

      const payload = {
        ...metadata,
        providerMetadata: {
          ...(metadata.providerMetadata || {}),
          ...(selectedPriceId ? { priceID: selectedPriceId } : {}),
          ...(selectedBillingCycle
            ? { billingCycle: selectedBillingCycle }
            : {}),
        },
      };

      try {
        await createSubscription(payload).unwrap();
        sessionStorage.setItem('subscriptionConfirmed', 'true');
        setStatus('confirm-payment');
      } catch (subscribeError) {
        setFailureDetail(
          getErrorMessage(subscribeError) ||
            'Payment was completed, but we could not finish activation. Please try again.'
        );
        setStatus('failed');
      }
    }

    confirmPayment();
  }, [
    sessionID,
    item,
    createSubscription,
    error,
    isDowngrade,
    isTransactionFetching,
    isTransactionLoading,
  ]);

  const handleGoHome = () => {
    navigate('/', { replace: true });
  };

  const handleTryAgain = () => {
    if (sessionID) {
      sessionStorage.removeItem('subscriptionConfirmed');
      lastConfirmedSessionRef.current = null;
      window.location.reload();
      return;
    }
    navigate('/subscriptions/subscribe', { replace: true });
  };

  return (
    <Card className='w-full max-w-md'>
      <div className='text-center p-6'>
        {status === 'lost' ? (
          <>
            {/* Icon */}
            <div className='mb-4'>
              <i className='pi pi-exclamation-triangle text-6xl text-orange-500'></i>
            </div>

            {/* Title */}
            <Typography className='text-2xl font-bold text-900'>
              Uh-oh! It seems like you're lost.
            </Typography>

            {/* Description */}
            <Typography className='text-600 line-height-3 mb-8'>
              We couldn't find a valid subscription session. Please try again.
            </Typography>

            {/* Action Buttons */}
            <div className='flex flex-column gap-3 align-items-center'>
              <Button
                label='Try Again'
                icon='pi pi-refresh'
                onClick={handleTryAgain}
                severity='info'
              />
              <Button
                label='Go Home'
                icon='pi pi-home'
                onClick={handleGoHome}
                outlined
              />
            </div>
          </>
        ) : status === 'confirm-payment' ? (
          <>
            {/* Icon */}
            <div className='mb-4'>
              <i className='pi pi-check-circle text-6xl text-green-500'></i>
            </div>

            {/* Title */}
            <Typography className='text-2xl font-bold text-900'>
              Payment Successful!
            </Typography>

            {/* Description */}
            <Typography className='text-600 line-height-3 mb-8'>
              Your subscription has been activated successfully. Welcome aboard!
            </Typography>

            {/* Action Buttons */}
            <div className='flex flex-column gap-3 align-items-center'>
              <Button
                label='Go to Dashboard'
                icon='pi pi-home'
                onClick={handleGoHome}
                severity='success'
              />
            </div>
          </>
        ) : status === 'failed' ? (
          <>
            <div className='mb-4'>
              <i className='pi pi-times-circle text-6xl text-red-500'></i>
            </div>

            <Typography className='text-2xl font-bold text-900'>
              We could not complete activation
            </Typography>

            <Typography className='text-600 line-height-3 mb-8'>
              {failureDetail ||
                'Please try again. If this continues, contact support.'}
            </Typography>

            <div className='flex flex-column gap-3 align-items-center'>
              <Button
                label='Try Again'
                icon='pi pi-refresh'
                onClick={handleTryAgain}
                severity='info'
              />
              <Button
                label='Go Home'
                icon='pi pi-home'
                onClick={handleGoHome}
                outlined
              />
            </div>
          </>
        ) : status === 'confirm-downgrade' ? (
          <>
            {/* Icon */}
            <div className='mb-4'>
              <i className='pi pi-check-circle text-6xl text-green-500'></i>
            </div>

            {/* Title */}
            <Typography className='text-2xl font-bold text-900'>
              Subscription Updated!
            </Typography>

            {/* Description */}
            <Typography className='text-600 line-height-3 mb-8'>
              You will remain on your current plan until the end of the billing
              cycle, then switch to your new plan.
            </Typography>

            {/* Action Buttons */}
            <div className='flex flex-column gap-3 align-items-center'>
              <Button
                label='Go to Dashboard'
                icon='pi pi-home'
                onClick={handleGoHome}
                severity='success'
              />
            </div>
          </>
        ) : status === 'verifiying-payment' ? (
          <>
            {/* Icon */}
            <div className='mb-4'>
              <i className='pi pi-spin pi-spinner text-6xl text-blue-500'></i>
            </div>

            {/* Title */}
            <Typography className='text-2xl font-bold text-900'>
              Verifying Payment
            </Typography>

            {/* Description */}
            <Typography className='text-600 line-height-3 mb-8'>
              Please wait while we verify your payment...
            </Typography>
          </>
        ) : (
          <>
            {/* Icon */}
            <div className='mb-4'>
              <i className='pi pi-spin pi-spinner text-6xl text-blue-500'></i>
            </div>

            {/* Title */}
            <Typography className='text-2xl font-bold text-900'>
              Loading
            </Typography>

            {/* Description */}
            <Typography className='text-600 line-height-3 mb-8'>
              Please wait while we process your request...
            </Typography>
          </>
        )}
      </div>
    </Card>
  );
}
